import { FILE_CATEGORY, FILE_SOURCE, FILE_STATUS } from '@/types/api'
import type { UserFile } from '@/types/api'

const RENDERABLE_EXTENSION = 'obj'

type ModelState =
  | 'ready'
  | 'preparing'
  | 'failed'
  | 'needs-conversion'
  | 'download-only'

const DOWNLOAD_ONLY_EXTENSIONS = ['step', 'stp']

function isDownloadOnly(file: UserFile | null | undefined): boolean {
  return !!file && DOWNLOAD_ONLY_EXTENSIONS.includes(file.extension)
}

export function isRenderable(file: UserFile | null | undefined): boolean {
  return (
    !!file &&
    file.extension === RENDERABLE_EXTENSION &&
    file.source === FILE_SOURCE.uploaded &&
    file.status === FILE_STATUS.ready
  )
}

function isOutput(file: UserFile): boolean {
  return file.category === FILE_CATEGORY.render || file.category === FILE_CATEGORY.ar
}

const IMAGE_EXTENSIONS = ['png', 'jpeg', 'jpg', 'webp']

export function isImage(file: UserFile | null | undefined): boolean {
  return !!file && IMAGE_EXTENSIONS.includes(file.extension)
}

function isPreparing(file: UserFile | null | undefined): boolean {
  return !!file && file.status === FILE_STATUS.pending
}

export function isArReady(file: UserFile | null | undefined): boolean {
  return file?.category === FILE_CATEGORY.ar && file.status === FILE_STATUS.ready
}

export function modelState(file: UserFile): ModelState {
  if (isOutput(file) && !isPreparing(file)) return 'ready'

  if (file.status === FILE_STATUS.failed) return 'failed'
  if (isPreparing(file)) return 'preparing'
  if (isDownloadOnly(file)) return 'download-only'

  if (!isRenderable(file)) return 'needs-conversion'
  return 'ready'
}

const CANNOT_PREVIEW = 'This selection can not be previewed.'

export function unavailableReason(file: UserFile | null | undefined): string | null {
  if (!file) return 'Select a model to preview it.'
  if (isRenderable(file) || isImage(file)) return null
  return CANNOT_PREVIEW
}

interface StateBadge {
  label: string
  hint: string
}

export const STATE_BADGE: Record<Exclude<ModelState, 'ready'>, StateBadge> = {
  'needs-conversion': {
    label: 'OBJ Conversion Required',
    hint: 'Convert this model to OBJ to work with it.',
  },
  preparing: {
    label: 'Preparing',
    hint: 'This model is still being prepared.',
  },
  failed: {
    label: 'Failed',
    hint: 'This model could not be prepared. Nothing was charged for it.',
  },
  'download-only': {
    label: 'STEP',
    hint: CANNOT_PREVIEW,
  },
}
